<template>
  <div class="animated fadeIn">
    <b-card class="mb-2" body-class="p-2" header-class="py-2" :header="$t('shared.search')">
      <b-form inline>
        <b-input-group size="sm" class="m-1" :prepend="$t('shared.id')">
          <numeric-input v-model="search.id"/>
        </b-input-group>
      </b-form>
    </b-card>

    <b-card class="p-2" bg-variant="light" no-body>
      <b-table
        ref="table"
        class="mb-0 bg-white v-mid rounded"
        striped
        hover
        :current-page="currentPage"
        :per-page="perPage"
        :busy.sync="progressing"
        :fields="fields"
        :items="getItems"
        :filter="tableFilter"
        stacked="md"
        :empty-text="$t('shared.noRecords')"
        :empty-filtered-text="$t('shared.noMatchingRecords')"
        :show-empty="!initializing"
        @refreshed="initializing = false"
        @context-changed="getCount"
        sort-by="created"
        sort-desc
      >
        <template v-slot:cell(title)="data">
          <b>
            {{ $t('transports.transportRequestOf') }}
            {{ (data.item.bundlesCount * data.item.stock.bundleRate).toLocaleString() }}
            {{ data.item.stock.model.product.type.unit.name }}
            {{ data.item.stock.model.product.title }}
          </b>
        </template>

        <template v-slot:cell(created)="data">
          <date-display v-if="data.item.created" :value="data.item.created"/>
        </template>

        <template v-slot:cell(transportDate)="data">
          <date-display v-if="data.item.transportDate" :value="data.item.transportDate"/>
        </template>

        <template v-slot:cell(transportType)="data">
          <template v-if="data.item.courierId">
            {{ $t('transports.transportByCourier', { courier: data.item.courier && data.item.courier.companyName }) }}
          </template>

          <template v-else>
            {{ $t('transports.personalTransport') }}
          </template>
        </template>

        <template v-slot:cell(status)="data">
          <h5 class="m-0">
            <b-badge v-if="data.item.status == 0" variant="warning" class="py-2 px-3">
              {{ $t('transports.statuses.tranporting') }}
            </b-badge>
            <b-badge v-else variant="secondary" class="py-2 px-3">
              {{ $t('transports.statuses.transported') }}
            </b-badge>
          </h5>
        </template>

        <template v-slot:cell(actions)="data">
          <b-btn
            variant="outline-secondary"
            class="text-dark mb-2"
            block
            size="sm"
            :to="`./${data.item.id}`"
          >
            {{ $t('shared.details') }}
          </b-btn>

          <b-btn
            v-if="data.item.done"
            variant="outline-secondary"
            class="text-dark"
            block
            size="sm"
            target="_blank"
            :to="`/transports/${data.item.id}/transport-license`"
          >
            {{ $t('transports.transportLicense') }}
          </b-btn>

          <b-btn
            v-else
            variant="outline-secondary"
            class="text-dark"
            block
            size="sm"
            @click="toggleDetails(data.item)"
          >
            {{ $t('transports.transportLicenses') }}

            <font-awesome-icon
              fixed-width
              class="align-middle"
              :icon="data.detailsShowing ? 'angle-up' : 'angle-down'"
            />
          </b-btn>
        </template>

        <template #row-details="data">
          <licenses :transport="data.item" :transport-index="data.index"/>
        </template>
      </b-table>
    </b-card>

    <b-pagination
      v-if="count > perPage"
      :total-rows="count"
      v-model="currentPage"
      :per-page="perPage"
      align="center"
    />
  </div>
</template>

<script>
import Licenses from './licenses/List'
import Breadcrumb from '@/mixins/breadcrumb'
import { serialize } from '@/lib/util'
import { DateDisplay } from '@/components/shared'

export default {
  name: 'Transports',
  mixins: [Breadcrumb],
  components: {
    DateDisplay,
    Licenses
  },
  data () {
    return {
      initializing: true,
      progressing: false,
      currentPage: Number(this.$route.query.p) || 1,
      count: 0,
      items: [],
      fields: [
        {
          key: 'id',
          sortable: true,
          label: this.$t('shared.id'),
          thStyle: 'width:30px'
        },
        {
          key: 'title',
          label: this.$t('shared.title'),
          thStyle: 'width:290px'
        },
        {
          key: 'created',
          sortable: true,
          label: this.$t('shared.created')
        },
        {
          key: 'transportDate',
          sortable: true,
          label: this.$t('transports.transportDate')
        },
        {
          key: 'transportType',
          label: this.$t('transports.transportType')
        },
        {
          key: 'status',
          label: this.$t('shared.status'),
          thStyle: 'width:130px'
        },
        {
          key: 'actions',
          label: ' ',
          thStyle: 'width:180px'
        }
      ],
      search: {
        id: null
      }
    }
  },
  computed: {
    tableFilter () {
      return Object.keys(this.search).find(k => this.search[k] !== null) ? serialize(this.search) : null
    },
    perPage () {
      return Number(this.$route.query.s) || this.$config.defaultPageSize
    }
  },
  watch: {
    currentPage () {
      this.syncUrl()
    },
    perPage () {
      this.syncUrl()
    }
  },
  mounted () {
    this.setBreadcrumb()
  },
  methods: {
    setBreadcrumb () {
      const items = []
      let path = ''

      path = '/transports'
      items.push({ path, text: this.$t('nav.transports') })

      this.commitBreadcrumb(items)
    },
    refresh () {
      this.$refs.table.refresh()
    },
    syncUrl () {
      let qs = {}
      if (this.currentPage > 1) qs.p = this.currentPage
      if (this.perPage !== this.$config.defaultPageSize) qs.s = this.perPage
      if (this.id) qs.id = this.id

      this.$router.push(`?${serialize(qs)}`)
    },
    filter (ctx, isCount) {
      let filter = {}

      if (!isCount) {
        filter.skip = (ctx.currentPage - 1) * ctx.perPage

        filter.limit = ctx.perPage

        if (ctx.sortBy) {
          filter.sort = ctx.sortDesc ? ctx.sortBy : `${ctx.sortBy}-asc`
        }

        filter.include = ['Courier', 'Stock']
      }

      if (this.search.id) {
        filter.id = this.search.id
      }

      return filter
    },
    getCount (ctx) {
      this.$api.transports.count(this.filter(ctx, true))
      .then(res => {
        this.count = res
      })
      .catch(err => {
        this.$showError(this.$getLocaleErrorMessage(err, 'transports'))
      })
    },
    getItems (ctx) {
      // ctx.currentPage
      // ctx.perPage
      // ctx.filter
      // ctx.sortBy
      // ctx.sortDesc
      if (this.initializing) this.getCount(ctx)

      return this.$api.transports.getAll(this.filter(ctx, false), true)
      .then(items => {
        return this.items = items
      })
      .catch(err => {
        this.$showError(this.$getLocaleErrorMessage(err, 'transports'))
        this.items = []
        return []
      })
    },
    toggleDetails (row) {
      this.$set(row, '_showDetails', !row._showDetails)
    }
  }
}
</script>